import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';

// Maps common spellings of units found in old combined qty strings to the standard labels
const UNIT_MAP = {
    kg: 'Kgs', 'kg.': 'Kgs', kgs: 'Kgs', 'kgs.': 'Kgs', kilogram: 'Kgs', kilograms: 'Kgs',
    mt: 'MT', 'm.t': 'MT', 'm.t.': 'MT', ton: 'MT', tons: 'MT', tonne: 'MT', tonnes: 'MT',
    g: 'Grams', gm: 'Grams', gms: 'Grams', gram: 'Grams', grams: 'Grams',
    l: 'Ltrs', ltr: 'Ltrs', ltrs: 'Ltrs', litre: 'Ltrs', litres: 'Ltrs', liter: 'Ltrs', liters: 'Ltrs',
    no: 'Nos', nos: 'Nos', 'nos.': 'Nos', pc: 'Nos', pcs: 'Nos', piece: 'Nos', pieces: 'Nos',
    drum: 'Drums', drums: 'Drums', bag: 'Bags', bags: 'Bags',
};
const normalizeUnit = (text) => {
    const trimmed = text.trim();
    return UNIT_MAP[trimmed.toLowerCase()] || trimmed;
};

// Parses an old combined value like "1,000.5 Kgs" -> { qty: "1000.5", unit: "Kgs" }.
// Returns a cleared value for NA-style entries and null when it cannot parse.
const parseLegacyQty = (text) => {
    const raw = String(text).trim();
    if (!raw || /^n\.?\/?a\.?$/i.test(raw)) return { qty: '', unit: '', note: 'cleared (was NA)' };
    const match = raw.match(/^([\d.,]+)\s*(.*)$/);
    if (!match) return null;
    const qty = parseFloat(match[1].replace(/,/g, ''));
    if (isNaN(qty)) return null;
    return { qty: String(qty), unit: normalizeUnit(match[2]) };
};

// New records store qty and unit separately; old records have one combined string in qtyDispatched
const formatQty = (record) => {
    if (!record.qtyDispatched) return 'NA';
    return record.qtyUnit ? `${record.qtyDispatched} ${record.qtyUnit}` : record.qtyDispatched;
};

const CoaDetailModal = ({ record, onClose }) => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ backgroundColor: 'white', padding: '20px 30px', borderRadius: '8px', width: '80%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>Details for Batch: {record.batchNumber}</h3>
            <p><strong>Customer:</strong> {record.customerCode || record.customerName}</p>
            {record.purpose && <p><strong>Purpose:</strong> {record.purpose}</p>}
            {record.courierName && <p><strong>Courier / Logistics:</strong> {record.courierName}</p>}
            {record.trackingNumber && <p><strong>Tracking Number:</strong> {record.trackingNumber}</p>}
            <p><strong>Manufacturing Date:</strong> {record.mfgDate}</p>
            <p><strong>Date of Dispatch:</strong> {record.dispatchDate || record.expDate}</p>
            <p><strong>Created:</strong> {record.createdAt ? new Date(record.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</p>
            {/* UPDATED: Display Quantity Dispatched with fallback to NA */}
            <p><strong>Qty Dispatched:</strong> {formatQty(record)}</p>
            
            <hr style={{ margin: '15px 0' }} />
            <h4>Analysis Results:</h4>
            <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th>Test Parameter</th><th>Specification</th><th>Result</th></tr></thead>
                <tbody>{record.analysisResults.map((res, index) => (<tr key={index}><td>{res.test}</td><td>{res.specification}</td><td>{res.result}</td></tr>))}</tbody>
            </table>
            <hr style={{ margin: '15px 0' }} />
            <p><a href={record.fileUrl} target="_blank" rel="noopener noreferrer">View/Download Attached Document ({record.fileName})</a></p>
            <button onClick={onClose} style={{ marginTop: '20px' }}>Close</button>
        </div>
    </div>
);

function CoaDashboard({ sample = false }) {
    // Sample COAs (pre-shipment / approvals) live in a parallel collection with the same shape
    const recordsCollection = sample ? 'sample_coa_records' : 'coa_records';
    const editPathBase = sample ? '/sample-coa/edit' : '/coa/edit';

    const [allRecords, setAllRecords] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [productFilter, setProductFilter] = useState('All');
    const [customerFilter, setCustomerFilter] = useState('All');
    const [fyFilter, setFyFilter] = useState('All');
    const [purposeFilter, setPurposeFilter] = useState('All');
    const [sortOrder, setSortOrder] = useState('createdAt_desc');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [reconcilePreview, setReconcilePreview] = useState(null);
    const [reconcileLoading, setReconcileLoading] = useState(false);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [productsSnapshot, customersSnapshot] = await Promise.all([
                    getDocs(collection(db, 'products')),
                    getDocs(collection(db, 'customers'))
                ]);
                setProducts(productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setCustomers(customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching master data:", error);
            }
            // Fetched separately so a rules problem on the records collection still leaves the filters usable
            try {
                const recordsSnapshot = await getDocs(query(collection(db, recordsCollection), orderBy('createdAt', 'desc')));
                setAllRecords(recordsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error(`Error reading ${recordsCollection}:`, error);
                alert(`Could not read records from "${recordsCollection}" - check Firestore rules.`);
            }
            setLoading(false);
        };
        fetchAllData();
    }, [recordsCollection]);

    // Financial year runs 1 April - 31 March; label e.g. "26-27". Use the stored value,
    // or derive it from the record's creation date for older records that predate the field.
    const recordFinancialYear = (record) => {
        if (record.financialYear) return record.financialYear;
        if (!record.createdAt?.seconds) return null;
        const date = new Date(record.createdAt.seconds * 1000);
        const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
        return `${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;
    };

    const financialYears = useMemo(() => {
        const set = new Set(allRecords.map(recordFinancialYear).filter(Boolean));
        return Array.from(set).sort().reverse();
    }, [allRecords]);

    const filteredRecords = useMemo(() => {
        let processed = [...allRecords];
        if (productFilter !== 'All') processed = processed.filter(r => r.productId === productFilter);
        if (customerFilter !== 'All') processed = processed.filter(r => r.parameters.customerId === customerFilter);
        if (fyFilter !== 'All') processed = processed.filter(r => recordFinancialYear(r) === fyFilter);
        if (sample && purposeFilter !== 'All') processed = processed.filter(r => r.purpose === purposeFilter);
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            processed = processed.filter(r =>
                r.batchNumber.toLowerCase().includes(lowercasedTerm) ||
                r.productName.toLowerCase().includes(lowercasedTerm) ||
                r.customerName?.toLowerCase().includes(lowercasedTerm) ||
                r.customerCode?.toLowerCase().includes(lowercasedTerm)
            );
        }
        processed.sort((a, b) => {
            // Sort by dispatch date (YYYY-MM-DD strings compare correctly); tie-break by creation time
            const dateA = a.dispatchDate || a.expDate || '';
            const dateB = b.dispatchDate || b.expDate || '';
            if (dateA !== dateB) {
                return sortOrder === 'createdAt_asc' ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
            }
            const createdA = a.createdAt?.seconds || 0;
            const createdB = b.createdAt?.seconds || 0;
            return sortOrder === 'createdAt_asc' ? createdA - createdB : createdB - createdA;
        });
        return processed;
    }, [allRecords, searchTerm, productFilter, customerFilter, fyFilter, sortOrder, sample, purposeFilter]);

    // Batches from one dispatch (same product, customer, dispatch date, and purpose) collapse into one expandable row
    const groupedRecords = useMemo(() => {
        const groups = [];
        const byKey = new Map();
        filteredRecords.forEach(r => {
            const key = `${r.productId}|${r.parameters?.customerId || r.customerCode || ''}|${r.dispatchDate || r.expDate || ''}|${r.purpose || ''}`;
            if (byKey.has(key)) {
                byKey.get(key).records.push(r);
            } else {
                const group = { key, records: [r] };
                byKey.set(key, group);
                groups.push(group);
            }
        });
        return groups;
    }, [filteredRecords]);

    const toggleGroup = (key) => setExpandedGroups(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Old records are the ones with no qtyUnit field at all; new records always have one (possibly empty)
    const startReconcile = () => {
        const rows = allRecords
            .filter(r => r.qtyUnit === undefined && String(r.qtyDispatched ?? '').trim() !== '')
            .map(r => ({
                id: r.id,
                label: r.coaNumber || r.batchNumber,
                oldValue: r.qtyDispatched,
                parsed: parseLegacyQty(r.qtyDispatched),
            }));
        setReconcilePreview(rows);
    };

    const applyReconcile = async () => {
        const updatable = reconcilePreview.filter(row => row.parsed);
        setReconcileLoading(true);
        try {
            for (const row of updatable) {
                await updateDoc(doc(db, 'coa_records', row.id), { qtyDispatched: row.parsed.qty, qtyUnit: row.parsed.unit });
            }
            setAllRecords(prev => prev.map(r => {
                const row = updatable.find(u => u.id === r.id);
                return row ? { ...r, qtyDispatched: row.parsed.qty, qtyUnit: row.parsed.unit } : r;
            }));
            alert(`${updatable.length} record(s) updated successfully.`);
            setReconcilePreview(null);
        } catch (error) {
            console.error("Error reconciling quantities:", error);
            alert("Update failed partway through. Records already updated are safe - run Reconcile again to finish the rest.");
        } finally {
            setReconcileLoading(false);
        }
    };

    const handleView = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    const handleDelete = async (record) => {
        if (!window.confirm(`Are you sure you want to delete the COA for batch ${record.batchNumber}? This action is permanent.`)) return;
        
        try {
            if (record.filePath) {
                const fileRef = ref(storage, record.filePath);
                await deleteObject(fileRef);
            }
            await deleteDoc(doc(db, recordsCollection, record.id));
            alert('Record deleted successfully.');
            setAllRecords(prevRecords => prevRecords.filter(r => r.id !== record.id));
        } catch (error) {
            console.error("Error deleting record:", error);
            if (error.code === 'storage/object-not-found') {
                if(window.confirm("File not found in storage, but it will be deleted from the database. Continue?")) {
                    await deleteDoc(doc(db, recordsCollection, record.id));
                    alert('Record deleted from database successfully.');
                    setAllRecords(prevRecords => prevRecords.filter(r => r.id !== record.id));
                }
            } else {
                alert("Failed to delete record. See console for details.");
            }
        }
    };

    if (loading) return <p>Loading COA Records...</p>;

    return (
        <div>
            <h2>{sample ? 'Sample COA Records (Pre-shipment / Approvals)' : 'COA Records Dashboard'}</h2>
            {!sample && (
                <div style={{ marginBottom: '10px', textAlign: 'right' }}>
                    <button onClick={startReconcile} title="Split old combined quantity text (e.g. '1000 Kgs') into separate qty and unit">Reconcile Old Qty/Units</button>
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
                <input type="text" placeholder="Search by Batch, Product, Customer..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '8px' }} />
                <select value={productFilter} onChange={e => setProductFilter(e.target.value)} style={{ padding: '8px' }}>
                    <option value="All">Filter by Product</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.productName}</option>)}
                </select>
                <select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} style={{ padding: '8px' }}>
                    <option value="All">Filter by Customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{[c.name, c.companyCode].filter(Boolean).join(' - ')}</option>)}
                </select>
                {sample && (
                    <select value={purposeFilter} onChange={e => setPurposeFilter(e.target.value)} style={{ padding: '8px' }}>
                        <option value="All">Filter by Purpose</option>
                        <option value="Pre-shipment">Pre-shipment</option>
                        <option value="Approval">Approval</option>
                        <option value="R&D">R&D</option>
                        <option value="Other">Other</option>
                    </select>
                )}
                <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} style={{ padding: '8px' }}>
                    <option value="All">Filter by Financial Year</option>
                    {financialYears.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
                </select>
                <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={{ padding: '8px' }}>
                    <option value="createdAt_desc">Sort by Newest</option>
                    <option value="createdAt_asc">Sort by Oldest</option>
                </select>
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table border="1" cellPadding="8" style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
                    <thead>
                        {/* UPDATED: Added Qty Column */}
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <th>Dispatch Date</th><th>Product</th><th>Batch No.</th><th>Customer</th>{sample && <th>Purpose</th>}<th>Qty</th><th>COA Document</th><th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groupedRecords.length > 0 ? groupedRecords.map(group => {
                            const renderRecordRow = (record, isChild) => (
                                <tr key={record.id} style={isChild ? { backgroundColor: '#fafafa' } : undefined}>
                                    <td>{isChild ? '' : (record.dispatchDate || record.expDate || 'N/A')}</td>
                                    <td>{isChild ? '' : record.productName}</td>
                                    <td>{isChild ? `↳ ${record.batchNumber}` : record.batchNumber}</td>
                                    <td>{isChild ? '' : (record.customerCode || record.customerName)}</td>
                                    {sample && <td>{isChild ? '' : (record.purpose || '—')}</td>}
                                    <td>{formatQty(record)}</td>
                                    <td><a href={record.fileUrl} target="_blank" rel="noopener noreferrer">{record.fileName}</a></td>
                                    <td>
                                        <button onClick={() => handleView(record)}>View</button>
                                        <Link to={`${editPathBase}/${record.id}`} style={{ marginLeft: '10px' }}>Edit</Link>
                                        <button onClick={() => handleDelete(record)} style={{ marginLeft: '10px', backgroundColor: 'darkred', color: 'white' }}>Delete</button>
                                    </td>
                                </tr>
                            );

                            if (group.records.length === 1) return renderRecordRow(group.records[0], false);

                            const first = group.records[0];
                            const isExpanded = expandedGroups.has(group.key);
                            const sharedFile = group.records.every(r => r.fileUrl === first.fileUrl);
                            // Total qty only when every batch has a numeric qty in the same unit
                            let totalQty = null;
                            if (first.qtyUnit && group.records.every(r => r.qtyUnit === first.qtyUnit)) {
                                const nums = group.records.map(r => parseFloat(r.qtyDispatched));
                                if (nums.every(n => !isNaN(n))) totalQty = `${nums.reduce((a, b) => a + b, 0)} ${first.qtyUnit}`;
                            }
                            return (
                                <React.Fragment key={group.key}>
                                    <tr style={{ backgroundColor: '#eef4fb', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => toggleGroup(group.key)}>
                                        <td>{isExpanded ? '▼' : '▶'} {first.dispatchDate || first.expDate || 'N/A'}</td>
                                        <td>{first.productName}</td>
                                        <td>{group.records.length} batches: {group.records.map(r => r.batchNumber).join(', ')}</td>
                                        <td>{first.customerCode || first.customerName}</td>
                                        {sample && <td>{first.purpose || '—'}</td>}
                                        <td>{totalQty || '—'}</td>
                                        <td>{sharedFile ? <a href={first.fileUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>{first.fileName}</a> : `${group.records.length} documents`}</td>
                                        <td><button onClick={e => { e.stopPropagation(); toggleGroup(group.key); }}>{isExpanded ? 'Collapse' : 'Expand'}</button></td>
                                    </tr>
                                    {isExpanded && group.records.map(record => renderRecordRow(record, true))}
                                </React.Fragment>
                            );
                        }) : (
                            <tr><td colSpan={sample ? 8 : 7} style={{ textAlign: 'center', padding: '20px' }}>No records match your criteria.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && <CoaDetailModal record={selectedRecord} onClose={() => setIsModalOpen(false)} />}

            {reconcilePreview && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', padding: '20px 30px', borderRadius: '8px', width: '80%', maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3>Reconcile Old Quantity Values</h3>
                        {reconcilePreview.length === 0 ? (
                            <p>All records already have separate quantity and unit. Nothing to update.</p>
                        ) : (
                            <>
                                <p>
                                    {reconcilePreview.filter(r => r.parsed).length} record(s) will be updated.
                                    {reconcilePreview.some(r => !r.parsed) && ` ${reconcilePreview.filter(r => !r.parsed).length} could not be parsed and will be left unchanged.`}
                                    {' '}Review the changes below before applying.
                                </p>
                                <table border="1" cellPadding="6" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr style={{ backgroundColor: '#f0f0f0' }}><th>COA / Batch</th><th>Current Value</th><th>New Qty</th><th>New Unit</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {reconcilePreview.map(row => (
                                            <tr key={row.id} style={!row.parsed ? { backgroundColor: '#fff3f3' } : undefined}>
                                                <td>{row.label}</td>
                                                <td>{String(row.oldValue)}</td>
                                                <td>{row.parsed ? row.parsed.qty || '—' : '—'}</td>
                                                <td>{row.parsed ? row.parsed.unit || '—' : '—'}</td>
                                                <td>{row.parsed ? (row.parsed.note || 'OK') : 'Cannot parse — left unchanged'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                        <div style={{ marginTop: '20px' }}>
                            {reconcilePreview.some(r => r.parsed) && (
                                <button onClick={applyReconcile} disabled={reconcileLoading} style={{ padding: '8px 16px' }}>
                                    {reconcileLoading ? 'Updating...' : `Apply ${reconcilePreview.filter(r => r.parsed).length} Update(s)`}
                                </button>
                            )}
                            <button onClick={() => setReconcilePreview(null)} disabled={reconcileLoading} style={{ marginLeft: '10px', padding: '8px 16px' }}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CoaDashboard;